import { Fragment, memo, type ReactNode } from 'react';
import {
  flexRender,
  PaginationState,
  RowSelectionState,
  ColumnPinningState,
  Table,
} from '@tanstack/react-table';
import { TableBody, TableCell, Skeleton, TableRow, cn } from '@sib/ui';
import { useIsLoadingDelayed } from '@sib/ui/hooks';
import { getRowStyling, getTableColumnPinningProperties } from '../../utils';
import {
  DataTableDelayedLoadingIndicator,
  DataTableDelayedLoadingIndicatorProps,
} from './DataTableDelayedLoadingIndicator';
import { DataTablePinnedCell } from './DataTablePinnedCell';
import { DataTableRowTooltip } from './DataTableRowTooltip';

interface DataTableBodyProps<T> extends Pick<PaginationState, 'pageSize'> {
  table: Table<T>;
  rowSelection: RowSelectionState;
  columnPinning: ColumnPinningState;
  loading?: boolean;
  withDelayedLoading?: boolean;
  isEmpty?: boolean;
  emptyPlaceholder?: React.ReactNode;
  TableDelayedLoadingIndicatorProps: DataTableDelayedLoadingIndicatorProps;
  onRowClick?: (row: T) => void;
  getIsRowClickable?: (row: T) => boolean;
  withFixedColumnWidth?: boolean;
  getIsRowDisabled?: (row: T) => boolean;
  getRowDisabledTooltip?: (row: T) => string;
  getRowClassName?: (row: T) => string | null;
  getRowGroupHeader?: (row: T, index: number) => ReactNode | null;
}

export const DataTableBody = <T,>({
  table,
  withDelayedLoading,
  emptyPlaceholder,
  isEmpty,
  pageSize,
  loading = false,
  TableDelayedLoadingIndicatorProps,
  onRowClick,
  getIsRowClickable,
  withFixedColumnWidth = false,
  getIsRowDisabled,
  getRowDisabledTooltip,
  getRowClassName,
  getRowGroupHeader,
}: DataTableBodyProps<T>) => {
  const isLoadingDelayed = useIsLoadingDelayed({
    loading,
  });

  const visibleLeafColumns = table.getVisibleLeafColumns();

  return (
    <TableBody className="relative">
      {loading ? (
        <>
          {withDelayedLoading && (
            <DataTableDelayedLoadingIndicator
              {...TableDelayedLoadingIndicatorProps}
              visible={isLoadingDelayed}
              isEmpty={isEmpty}
            />
          )}

          {Array.from({
            length: pageSize,
          }).map((_, index) => (
            <TableRow
              key={`table-row-skeleton-${index}`}
              className="transition-none"
            >
              {visibleLeafColumns.map(column => (
                <DataTablePinnedCell
                  key={`table-skeleton-${index}-${column.id}`}
                  column={column}
                  withFixedColumnWidth={withFixedColumnWidth}
                >
                  <Skeleton className="bg-surface-soft block h-6.5 w-full max-w-full rounded-sm" />
                </DataTablePinnedCell>
              ))}
            </TableRow>
          ))}
        </>
      ) : !isEmpty ? (
        table.getRowModel().rows.map((row, index) => {
          const canRowExpand = row.getCanExpand();
          const isRowClickable =
            Boolean(onRowClick) && (getIsRowClickable?.(row.original) ?? true);
          const handleRowClick = isRowClickable
            ? () => onRowClick?.(row.original)
            : canRowExpand && (() => row.toggleExpanded());
          const isRowDisabled = getIsRowDisabled?.(row.original) ?? false;

          const tableRow = (
            <TableRow
              key={row.id}
              {...(handleRowClick && {
                onClick: handleRowClick,
              })}
              className={cn(
                'transition-none',
                (canRowExpand || isRowClickable) &&
                  !isRowDisabled &&
                  'cursor-pointer',
                getRowStyling({
                  row,
                  index,
                  rows: table.getRowModel().rows,
                }),
                getRowClassName?.(row.original)
              )}
            >
              {row.getVisibleCells().map(cell => {
                const cellContext = cell.getContext();
                const className = cellContext.column.columnDef.meta?.className;

                const aplicableClassName =
                  typeof className === 'string'
                    ? className
                    : className?.(row.original);

                const excludeFromDisabledRowStyle =
                  cell.column.columnDef.meta?.excludeFromDisabledRowStyle;
                const showDisabledRowCellStyle =
                  isRowDisabled && !excludeFromDisabledRowStyle;
                const { pinPosition } = getTableColumnPinningProperties(
                  cell.column
                );

                return (
                  <DataTablePinnedCell
                    key={cell.id}
                    column={cell.column}
                    withFixedColumnWidth={withFixedColumnWidth}
                    className={cn(
                      aplicableClassName,
                      showDisabledRowCellStyle &&
                        (pinPosition
                          ? 'data-table-row-disabled-pinned-cell'
                          : 'data-table-row-disabled-cell')
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cellContext)}
                  </DataTablePinnedCell>
                );
              })}
            </TableRow>
          );

          const renderedRow =
            isRowDisabled && getRowDisabledTooltip ? (
              <DataTableRowTooltip
                key={row.id}
                content={getRowDisabledTooltip(row.original)}
              >
                {tableRow}
              </DataTableRowTooltip>
            ) : (
              tableRow
            );

          const groupHeader = getRowGroupHeader?.(row.original, index);

          if (!groupHeader) return renderedRow;

          return (
            <Fragment key={row.id}>
              <TableRow className="transition-none hover:bg-transparent">
                <TableCell
                  colSpan={visibleLeafColumns.length}
                  className="h-auto max-h-none min-h-0 p-0"
                >
                  {groupHeader}
                </TableCell>
              </TableRow>
              {renderedRow}
            </Fragment>
          );
        })
      ) : (
        emptyPlaceholder || null
      )}
    </TableBody>
  );
};

export const MemoizedDataTableBody = memo(DataTableBody, (prev, next) => {
  const areStatesEqual =
    JSON.stringify(prev.rowSelection) === JSON.stringify(next.rowSelection) &&
    JSON.stringify(prev.table.getState().columnSizing) ===
      JSON.stringify(next.table.getState().columnSizing) &&
    prev.columnPinning.left?.join() === next.columnPinning.left?.join() &&
    prev.columnPinning.right?.join() === next.columnPinning.right?.join() &&
    prev.emptyPlaceholder === next.emptyPlaceholder;

  return (
    prev.table.options.data === next.table.options.data &&
    prev.isEmpty === next.isEmpty &&
    prev.loading === next.loading &&
    prev.withFixedColumnWidth === next.withFixedColumnWidth &&
    prev.getIsRowDisabled === next.getIsRowDisabled &&
    prev.getRowDisabledTooltip === next.getRowDisabledTooltip &&
    prev.getRowClassName === next.getRowClassName &&
    prev.getRowGroupHeader === next.getRowGroupHeader &&
    prev.getIsRowClickable === next.getIsRowClickable &&
    areStatesEqual
  );
}) as typeof DataTableBody;
